import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { Loader, useQueryFetch } from 'sonata-react-components';

import { getInsights } from '../../../services/insights.service';

import './insights.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { InsightType } from './insights.types';

type Props = {
  user: UserInterface & { businessInsights?: Record<string, any>; id: string };
  type: InsightType;
};

export function Insights({ user, type }: Readonly<Props>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: snippetConfig } = useQueryFetch(
    ['insightsConfig', type, user],
    async () => getInsights(user.id, type),
    { cacheTime: 0 }
  );

  const getSnippet = () => {
    if (!snippetConfig) {
      return null;
    }

    const userHasBusinessUnit = user.businessInsights && !isEmpty(user.businessInsights.businessUnit);
    const businessUnitUsers = userHasBusinessUnit ? user.businessInsights?.businessUnit?.users?.toString() : '';
    const revertParam = type === 'insights' ? '<param name="revert" value="sorts" />' : '';
    const snippet = `
      <script type="text/javascript" src="https://tableau.sonataplatform.com/javascripts/api/viz_v1.js"></script>
      <div class="tableauPlaceholder">
        <object class="tableauViz" style="display: none;">
          <param name="host_url" value="${snippetConfig?.hostUrl}" />
          <param name="embed_code_version" value="3" />
          <param name="site_root" value="${snippetConfig.siteRoot}" />
          <param name="name" value="${snippetConfig.viewName}" />
          <param name="tabs" value="no" />
          <param name="showShareOptions" value="false" />
          <param name="toolbar" value="top" />
          <param name="showAppBanner" value="false" />
          <param name="filter" value="iframeSizedToWindow=true" />
          <param name="filter" value="businessUnitUsers=${businessUnitUsers}"/>
          ${revertParam}
          <param name="ticket" value="${snippetConfig.ticket}" />
        </object>
      </div>
    `;

    return snippet;
  };

  useEffect(() => {
    const html = getSnippet();
    if (!html || !containerRef.current) {
      return;
    }
    containerRef.current.innerHTML = html;
    const scripts = Array.from(containerRef.current.querySelectorAll('script')) as HTMLScriptElement[];
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    // Fallback: if the Tableau API still isn't present, ensure it's loaded once
    const src = 'https://tableau.sonataplatform.com/javascripts/api/viz_v1.js';
    if (!(window as any).tableau) {
      const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
      if (!existing) {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        document.body.appendChild(s);
      }
    }
  }, [snippetConfig, user, type]);

  const snippet = getSnippet();

  return (
    <div className="insights">
      <div className="insights__content">
        {!snippet && (
          <Loader
            backgroundColor="var(--color-sonata-secondary-white)"
            showBackground
            type="landing"
            variant="default"
          />
        )}
        <div className="insights__snippet" ref={containerRef}></div>
      </div>
    </div>
  );
}
